import React, { useEffect, useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { AddRoundBlueIcon } from '../../../../../assets/icons'
import { DeleteConfirmModal } from './DeleteConfirmModal'
import moment from 'moment'
import { styles } from './styles'
import { invoices } from '@linqpal/models/src/routes'
import { SavedNotes } from './noteTypes'
import { NotesList } from './NotesList'
import { useTranslation } from 'react-i18next'
import { BtText, BtInput } from '@linqpal/components/src/ui'
import { routes2 } from '@linqpal/models'
import projectsStore from '../../../../Projects/Store'
import { observer, Observer } from 'mobx-react'

const dateFormat = 'MMM DD, YYYY [at] hh:mmA z'
type Props = {
  invoice?: any
  project?: any
  isItInvoiceCreate?: boolean //if flag is true we using this component for invoiceCreateSidebar else for InvoiceViewSidebar
}

const NotesComponent = observer(
  ({ invoice, project, isItInvoiceCreate }: Props) => {
    const { t } = useTranslation('global')
    const [notesVisible, setNotesVisible] = useState<boolean>(false)
    const [note, setNote] = useState<string>('')
    const [savedNotes, setSavedNotes] = useState<SavedNotes[]>([])
    const [visible, setVisible] = useState<boolean>(false)
    const [itemToDelete, setItemToDelete] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const { setNoteIsTypingNow } = projectsStore
    const saveNote = async () => {
      setNotesVisible(false)

      if (!isItInvoiceCreate) {
        const resp = await invoices.invoiceNotesSave({
          message: note,
          invoiceId: invoice.id,
        })
        resp?.result === 'ok' &&
          setSavedNotes((prev) => [
            {
              message: note,
              createdDate: moment().format(dateFormat),
              id: resp.invoiceNoteId,
            },
            ...prev,
          ])
      } else {
        invoice.setNotes(note)
        setNotesVisible(false)
        setNote('')
      }

      setNote('')
    }

    const saveNoteForProject = async () => {
      setNotesVisible(false)
      setNoteIsTypingNow(false)
      const resp = await routes2.project.createNote({
        message: note,
        projectId: project._id,
      })
      resp?.result === 'ok' &&
        setSavedNotes((prev) => [
          {
            message: note,
            createdDate: moment().format(dateFormat),
            id: resp.data._id,
          },
          ...prev,
        ])
      setNote('')
    }

    const deleteNote = async () => {
      if (isItInvoiceCreate) {
        setIsLoading(true)
        invoice.deleteNoteFromNotes(itemToDelete)
        setIsLoading(false)
        setVisible(false)
        return
      }
      try {
        setIsLoading(true)
        const response = await invoices.invoiceNotesDelete(itemToDelete)
        if (response?.result === 'ok') {
          setSavedNotes((prev) => [
            ...prev.filter((el) => el.id !== itemToDelete),
          ])
        }
        setVisible(false)
      } catch (e) {
        setIsLoading(false)
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }

    const getNotes = async () => {
      if (invoice) {
        const getNotesResponse = await invoices.invoiceNotes(invoice.id)
        const reversedNotes = getNotesResponse?.invoiceNotes?.reverse()
        setSavedNotes(reversedNotes)
      } else {
        const getNotesResponse = await routes2.project.getNotes(project._id)
        const reversedNotes = getNotesResponse?.items?.reverse().map((item) => {
          return {
            ...item,
            createdDate: moment(item.createdAt).format(dateFormat),
          }
        })
        setSavedNotes(reversedNotes)
      }
    }

    const deleteNoteInProject = async () => {
      try {
        setIsLoading(true)
        const response = await routes2.project.deleteNote(itemToDelete)
        if (response?.result === 'ok') {
          getNotes()
        }
        setVisible(false)
      } catch (e) {
        setIsLoading(false)
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }

    useEffect(() => {
      !isItInvoiceCreate && getNotes()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <View style={styles.container} testID="notes_main_wrappe_component">
        <View style={styles.notesHeader} testID="notes_header">
          <BtText style={styles.title} testID="notes_label">
            {t('InvoiceNotes.notes')}
          </BtText>
          <TouchableOpacity
            style={[styles.addContainer, notesVisible && { opacity: 0.6 }]}
            activeOpacity={0.6}
            onPress={() => {
              if (!notesVisible) {
                setNotesVisible(true)
                setNoteIsTypingNow(true)
              }
            }}
          >
            <AddRoundBlueIcon width={18} height={18} />
            <BtText style={styles.addNoteText}>
              {t('InvoiceNotes.add_new_note')}
            </BtText>
          </TouchableOpacity>
        </View>
        {notesVisible ? (
          <View>
            <BtInput
              style={styles.textArea}
              placeholder={t('InvoiceNotes.write_something')}
              textStyle={{ height: 100, paddingTop: 5 }}
              placeholderTextColor="#A7B1C3"
              multiline
              onChangeText={(text) => setNote(text)}
              value={note}
              testID="write_something_input"
            />
            {note.length ? (
              <TouchableOpacity
                style={styles.saveButton}
                onPress={invoice ? saveNote : saveNoteForProject}
              >
                <BtText style={styles.addNoteText}>
                  {t('InvoiceNotes.save')}
                </BtText>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : (
          !savedNotes.length && (
            <BtText style={styles.emptyNotesText}>
              {t('InvoiceNotes.here')}
            </BtText>
          )
        )}
        <Observer>
          {() => (
            <NotesList
              notes={!isItInvoiceCreate ? savedNotes : invoice.notes}
              setVisible={setVisible}
              setItemToDelete={setItemToDelete}
              isItInvoiceCreate={isItInvoiceCreate}
            />
          )}
        </Observer>

        <DeleteConfirmModal
          visible={visible}
          closeModal={() => setVisible(false)}
          deleteNote={invoice ? deleteNote : deleteNoteInProject}
          loading={isLoading}
        />
      </View>
    )
  },
)

export default NotesComponent
